import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ExternalLink, Radio } from "lucide-react";
import { api } from "@/lib/api";
import { BBC_LOGO_URL } from "@/lib/brand";
import "./LivePage.css";

const POLL_MS = 60 * 1000;
const validVideoId = (value) => typeof value === "string" && /^[A-Za-z0-9_-]{11}$/.test(value);

export default function LivePage() {
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [videoId, setVideoId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [playerError, setPlayerError] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/livestream/status");
      setStatus(data);
      setApiError(false);
      if (data?.is_live && validVideoId(data.video_id)) {
        setVideoId(data.video_id);
        setPlayerError(false);
      }
    } catch {
      setApiError(true);
      setStatus({ is_live: false, state: "UNKNOWN" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, [refresh]);

  const active = status?.is_live && validVideoId(status?.video_id);
  const ended = !apiError && !active && !!videoId;
  const title = status?.title || location.state?.title || "BBC Live Broadcast";
  const watchUrl = videoId ? `https://www.youtube.com/watch?v=${videoId}` : null;

  return <div className="live-page min-h-screen bg-[#F3EFEA] text-[#1C201E]">
    <header className="live-header bg-white border-b border-[#E5E0D8]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4 h-full">
        <Link to="/" className="flex items-center gap-3 min-w-0 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2C4C3B]" aria-label="Bible Baptist Church Barani home">
          <img src={BBC_LOGO_URL} alt="" className="w-10 h-10 object-contain" />
          <span className="font-serif-display text-lg sm:text-xl truncate">BBC Barani</span>
        </Link>
        <nav aria-label="Live page navigation" className="flex items-center gap-4 text-sm">
          <Link to="/" className="text-[#2C4C3B] hover:underline focus-visible:underline">Home</Link>
          <Link to="/sermons" className="text-[#2C4C3B] hover:underline focus-visible:underline">Sermons</Link>
        </nav>
      </div>
    </header>
    <main className="live-main max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <div className="flex items-center gap-2 text-red-700 font-bold tracking-wide text-sm">
        <Radio size={18} aria-hidden="true" /> {active ? "LIVE" : ended ? "BROADCAST ENDED" : "LIVE BROADCAST"}
      </div>
      <h1 className="font-serif-display text-3xl sm:text-4xl mt-2 mb-5 break-words">{title}</h1>
      {loading && <div role="status" className="rounded-xl bg-white border p-8">Checking live broadcast…</div>}
      {!loading && videoId && <>
        <div className="live-player-wrap rounded-xl overflow-hidden shadow-lg" data-testid="live-player">
          <iframe
            key={videoId}
            title={`${title} YouTube player`}
            src={`https://www.youtube-nocookie.com/embed/${videoId}?playsinline=1&rel=0`}
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            onError={() => setPlayerError(true)}
          />
        </div>
        {ended && <p role="status" className="mt-4 text-[#5C6661]">This live broadcast has ended. The recording may still be available in the player.</p>}
        {apiError && <p role="status" className="mt-4 text-[#5C6661]">Live status is temporarily unavailable. Playback may continue.</p>}
        {playerError && <p role="alert" className="mt-4 text-red-700">The embedded player could not load. You can watch on YouTube instead.</p>}
        <div className="mt-6 bg-white border border-[#E5E0D8] rounded-xl p-5 sm:p-6">
          <h2 className="font-serif-display text-xl">Broadcast information</h2>
          <p className="mt-2 font-medium">{title}</p>
          {status?.start_time && <p className="mt-1 text-sm text-[#5C6661]">Started: {status.start_time}</p>}
          {status?.description && <p className="mt-3 text-[#5C6661] whitespace-pre-line">{status.description}</p>}
          <a href={watchUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-5 text-sm text-[#2C4C3B] underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2C4C3B]">
            Watch on YouTube <ExternalLink size={15} aria-hidden="true" />
          </a>
        </div>
      </>}
      {!loading && !videoId && <div role="status" className="rounded-xl bg-white border border-[#E5E0D8] p-6 sm:p-8">
        <p className="text-lg font-medium">{apiError ? "Live status is temporarily unavailable." : status?.is_live ? "The live video is currently unavailable." : "No live broadcast is currently available."}</p>
        <div className="flex flex-wrap gap-4 mt-5">
          <button type="button" onClick={refresh} className="rounded-full px-5 py-2 bg-[#2C4C3B] text-white hover:bg-[#1A3326] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2C4C3B]">Try again</button>
          <Link to="/" className="inline-flex items-center gap-2 rounded-full px-5 py-2 border border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#E8EEE9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2C4C3B]"><ArrowLeft size={16} aria-hidden="true" /> Return Home</Link>
        </div>
      </div>}
    </main>
  </div>;
}
